import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { ConsoleShell } from "@/components/ConsoleShell";
import { useVehicles } from "@/context/VehicleContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["manufacturer", "model", "year", "trim", "mileage", "vin", "confirm"];
const YEARS = Array.from({ length: 40 }, (_, i) => new Date().getFullYear() + 1 - i);
const TRIMS = ["Base", "Sport", "Luxury", "Premium", "Limited", "Touring", "SE", "EX", "XLE", "Custom"];

export default function RegisterVehicle() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useVehicles();
  const [step, setStep] = useState(0);
  const [meta, setMeta] = useState({ brands: [], total: 0 });
  const [brandQuery, setBrandQuery] = useState("");
  const [models, setModels] = useState([]);
  const [modelQuery, setModelQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [vinBusy, setVinBusy] = useState(false);
  const [form, setForm] = useState({
    brand: "",
    model: "",
    year: new Date().getFullYear() - 5,
    trim: "Base",
    mileage: 45000,
    vin: "",
    vin_facts: null,
    wd_id: params.get("wd_id") || "",
    nickname: "",
    country: "",
    image_url: "",
    is_primary: true,
  });

  useEffect(() => {
    axios.get(`${API}/cars/meta`).then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  // Prefill from catalog
  useEffect(() => {
    const wd = params.get("wd_id");
    if (!wd) return;
    axios.get(`${API}/cars/${wd}`).then((r) => {
      const c = r.data;
      setForm((f) => ({
        ...f,
        brand: c.brand || "",
        model: c.model || "",
        wd_id: c.wd_id,
        country: c.country || "",
        image_url: c.image_url || "",
        nickname: `${c.brand} ${c.model}`,
      }));
      setStep(2); // jump to year if catalog picked
    }).catch(() => {});
  }, [params]);

  useEffect(() => {
    if (!form.brand) return;
    axios
      .get(`${API}/cars`, { params: { brand: form.brand, limit: 200 } })
      .then((r) => {
        const uniq = [];
        const seen = new Set();
        for (const c of r.data) {
          if (seen.has(c.model)) continue;
          seen.add(c.model);
          uniq.push(c);
        }
        setModels(uniq);
      })
      .catch(() => setModels([]));
  }, [form.brand]);

  const brands = useMemo(() => {
    const q = brandQuery.trim().toLowerCase();
    const list = meta.brands || [];
    if (!q) return list.slice(0, 80);
    return list.filter((b) => b.toLowerCase().includes(q)).slice(0, 80);
  }, [brandQuery, meta.brands]);

  const filteredModels = useMemo(() => {
    const q = modelQuery.trim().toLowerCase();
    if (!q) return models;
    return models.filter((m) => (m.model || "").toLowerCase().includes(q));
  }, [models, modelQuery]);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const decodeVin = async () => {
    if (!form.vin || form.vin.length < 5) return;
    setVinBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/vin/decode`, { vin: form.vin });
      setForm((f) => ({
        ...f,
        vin: data.vin || f.vin,
        vin_facts: data.fields || null,
        brand: data.fields?.make || f.brand,
        model: data.fields?.model || f.model,
        year: Number(data.fields?.model_year) || f.year,
        trim: data.fields?.trim || f.trim,
      }));
    } catch (e) {
      setError(e.response?.data?.detail || "VIN decode failed");
    } finally {
      setVinBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      let vehicle;
      if (form.wd_id) {
        const { data } = await axios.post(`${API}/vehicles/from-catalog`, {
          wd_id: form.wd_id,
          year: Number(form.year),
          trim: form.trim,
          mileage: Number(form.mileage),
          vin: form.vin || null,
          nickname: form.nickname || `${form.brand} ${form.model}`,
          is_primary: form.is_primary,
        });
        vehicle = data;
        if (form.vin && form.vin_facts) {
          await axios.post(`${API}/vehicles/${vehicle.id}/vin`, { vin: form.vin });
        }
      } else {
        const { data } = await axios.post(`${API}/vehicles`, {
          brand: form.brand,
          model: form.model,
          year: Number(form.year),
          trim: form.trim,
          mileage: Number(form.mileage),
          vin: form.vin || null,
          vin_facts: form.vin_facts,
          nickname: form.nickname || `${form.year} ${form.brand} ${form.model}`,
          country: form.country || null,
          image_url: form.image_url || null,
          is_primary: form.is_primary,
          engine: form.vin_facts?.engine_displacement_l || null,
          fuel: form.vin_facts?.fuel_type || null,
          transmission: form.vin_facts?.transmission || null,
        });
        vehicle = data;
      }
      await refresh();
      navigate(`/console/vehicle/${vehicle.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const canNext = () => {
    if (step === 0) return !!form.brand;
    if (step === 1) return !!form.model;
    if (step === 2) return !!form.year;
    if (step === 3) return !!form.trim;
    if (step === 4) return form.mileage >= 0;
    return true;
  };

  return (
    <ConsoleShell title="register vehicle">
      <div className="mx-auto max-w-3xl px-6 lg:px-10 pt-10 pb-24">
        <Link
          to="/console/garage"
          className="inline-flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest text-white/50 hover:text-cyan-electric"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> back to garage
        </Link>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
          <span className="chip-brand">/ register · vehicle profile</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tighter">
            Add your vehicle.
          </h1>
          <p className="mt-3 text-white/55 text-sm max-w-xl">
            Every AI decision locks to this registered profile - manufacturer, model, year, trim,
            mileage, and VIN. You will not need to restate ownership.
          </p>
        </motion.div>

        <div className="mt-8 flex flex-wrap gap-2">
          {STEPS.map((s, i) => (
            <button
              key={s}
              type="button"
              onClick={() => i <= step && setStep(i)}
              className={`font-mono-tech text-[9px] uppercase tracking-widest px-3 h-7 border ${
                i === step
                  ? "border-cyan-electric text-cyan-electric"
                  : i < step
                  ? "border-white/30 text-white/70"
                  : "border-white/10 text-white/30"
              }`}
            >
              {i + 1}. {s}
            </button>
          ))}
        </div>

        <div className="mt-8 border border-white/10 bg-obsidian/70 p-6 min-h-[320px]">
          {step === 0 && (
            <div>
              <h2 className="font-mono-tech text-[11px] uppercase tracking-widest text-white/50 mb-4">
                Manufacturer
              </h2>
              <input
                value={brandQuery}
                onChange={(e) => setBrandQuery(e.target.value)}
                placeholder="Search brands..."
                className="w-full bg-black/50 border border-white/15 focus:border-cyan-electric focus:outline-none px-4 h-11 font-mono-tech text-sm mb-4"
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
                {brands.map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => {
                      setForm((f) => ({ ...f, brand: b, model: "", wd_id: "" }));
                      next();
                    }}
                    className={`text-left px-3 py-3 border font-mono-tech text-xs uppercase tracking-widest ${
                      form.brand === b
                        ? "border-cyan-electric text-cyan-electric"
                        : "border-white/10 text-white/70 hover:border-white/30"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h2 className="font-mono-tech text-[11px] uppercase tracking-widest text-white/50 mb-4">
                Model · {form.brand}
              </h2>
              <input
                value={modelQuery}
                onChange={(e) => setModelQuery(e.target.value)}
                placeholder="Search models..."
                className="w-full bg-black/50 border border-white/15 focus:border-cyan-electric focus:outline-none px-4 h-11 font-mono-tech text-sm mb-4"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                {filteredModels.map((m) => (
                  <button
                    key={`${m.wd_id}-${m.model}`}
                    type="button"
                    onClick={() => {
                      setForm((f) => ({
                        ...f,
                        model: m.model,
                        wd_id: m.wd_id,
                        country: m.country || f.country,
                        image_url: m.image_url || "",
                        nickname: `${m.brand} ${m.model}`,
                      }));
                      next();
                    }}
                    className={`text-left px-3 py-3 border ${
                      form.model === m.model
                        ? "border-cyan-electric text-cyan-electric"
                        : "border-white/10 text-white/70 hover:border-white/30"
                    }`}
                  >
                    <div className="font-display text-lg tracking-tight">{m.model}</div>
                    <div className="font-mono-tech text-[9px] uppercase text-white/40">{m.country}</div>
                  </button>
                ))}
                {filteredModels.length === 0 && (
                  <div className="col-span-2">
                    <input
                      value={form.model}
                      onChange={(e) => setForm((f) => ({ ...f, model: e.target.value, wd_id: "" }))}
                      placeholder="Type model name manually"
                      className="w-full bg-black/50 border border-white/15 px-4 h-11 font-mono-tech text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="font-mono-tech text-[11px] uppercase tracking-widest text-white/50 mb-4">Year</h2>
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-72 overflow-y-auto">
                {YEARS.map((y) => (
                  <button
                    key={y}
                    type="button"
                    onClick={() => {
                      setForm((f) => ({ ...f, year: y }));
                      next();
                    }}
                    className={`h-11 border font-mono-tech text-sm ${
                      form.year === y
                        ? "border-cyan-electric text-cyan-electric"
                        : "border-white/10 text-white/70 hover:border-white/30"
                    }`}
                  >
                    {y}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-mono-tech text-[11px] uppercase tracking-widest text-white/50 mb-4">Trim</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {TRIMS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      setForm((f) => ({ ...f, trim: t }));
                      next();
                    }}
                    className={`h-11 border font-mono-tech text-xs uppercase tracking-widest ${
                      form.trim === t
                        ? "border-cyan-electric text-cyan-electric"
                        : "border-white/10 text-white/70"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <input
                className="mt-4 w-full bg-black/50 border border-white/15 px-4 h-11 font-mono-tech text-sm"
                value={form.trim}
                onChange={(e) => setForm((f) => ({ ...f, trim: e.target.value }))}
                placeholder="Or type custom trim"
              />
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="font-mono-tech text-[11px] uppercase tracking-widest text-white/50 mb-4">
                Current mileage
              </h2>
              <input
                type="number"
                min={0}
                value={form.mileage}
                onChange={(e) => setForm((f) => ({ ...f, mileage: Number(e.target.value) }))}
                className="w-full bg-black/50 border border-white/15 focus:border-cyan-electric focus:outline-none px-4 h-14 font-display text-3xl tracking-tighter"
              />
              <p className="mt-3 text-white/40 text-sm">Used to generate your maintenance schedule.</p>
            </div>
          )}

          {step === 5 && (
            <div>
              <h2 className="font-mono-tech text-[11px] uppercase tracking-widest text-white/50 mb-4">
                VIN (optional but recommended)
              </h2>
              <div className="flex gap-2">
                <input
                  value={form.vin}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, vin: e.target.value.toUpperCase().slice(0, 17) }))
                  }
                  placeholder="17-character VIN"
                  className="flex-1 bg-black/50 border border-white/15 px-4 h-11 font-mono-tech tracking-wider"
                />
                <button
                  type="button"
                  onClick={decodeVin}
                  disabled={vinBusy}
                  className="h-11 px-5 border border-cyan-electric/60 text-cyan-electric font-mono-tech text-[11px] uppercase tracking-widest"
                >
                  {vinBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Decode"}
                </button>
              </div>
              {form.vin_facts && (
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(form.vin_facts).slice(0, 8).map(([k, v]) => (
                    <div key={k} className="contents">
                      <span className="text-white/40 font-mono-tech text-[10px] uppercase">{k}</span>
                      <span className="text-white/80 font-mono-tech text-[11px] truncate">{v}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-3 text-white/40 text-xs">Skip if you do not have the VIN yet.</p>
            </div>
          )}

          {step === 6 && (
            <div>
              <h2 className="font-mono-tech text-[11px] uppercase tracking-widest text-white/50 mb-4">
                Confirm registration
              </h2>
              <input
                value={form.nickname}
                onChange={(e) => setForm((f) => ({ ...f, nickname: e.target.value }))}
                placeholder="Nickname"
                className="w-full bg-black/50 border border-white/15 px-4 h-11 font-mono-tech text-sm mb-4"
              />
              <div className="border border-white/10 bg-black/30 p-4 space-y-2 font-mono-tech text-xs uppercase tracking-widest text-white/70">
                <div>{form.year} · {form.brand} · {form.model}</div>
                <div>Trim · {form.trim}</div>
                <div>Mileage · {Number(form.mileage).toLocaleString()} mi</div>
                <div>VIN · {form.vin || "not provided"}</div>
              </div>
              <label className="mt-4 flex items-center gap-2 text-sm text-white/60">
                <input
                  type="checkbox"
                  checked={form.is_primary}
                  onChange={(e) => setForm((f) => ({ ...f, is_primary: e.target.checked }))}
                />
                Set as primary vehicle for AI context
              </label>
            </div>
          )}

          {error && (
            <div className="mt-4 text-mclaren font-mono-tech text-[10px] uppercase tracking-widest">
              {typeof error === "string" ? error : JSON.stringify(error)}
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            className="inline-flex items-center gap-2 h-11 px-4 border border-white/15 text-white/70 font-mono-tech text-[11px] uppercase tracking-widest disabled:opacity-30"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> back
          </button>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={next}
              disabled={!canNext()}
              className="inline-flex items-center gap-2 h-11 px-5 bg-mclaren text-void font-mono-tech text-[11px] uppercase tracking-widest disabled:opacity-40"
            >
              continue <ArrowRight className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="inline-flex items-center gap-2 h-11 px-5 bg-cyan-electric text-void font-mono-tech text-[11px] uppercase tracking-widest disabled:opacity-40"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              register vehicle
            </button>
          )}
        </div>
      </div>
    </ConsoleShell>
  );
}
